"use client";

import type { FC } from "react";
import { ListBulletIcon, MapIcon } from "@heroicons/react/24/outline";
import type { TaxonomyOption } from "cmw-client";
import useEmblaCarousel from "embla-carousel-react";
import { Button } from "@/components/button";
import { Tag } from "@/components/tag";
import { cn } from "@/lib/cn";
import { AllFiltersButton } from "./all-filters-button";
import { toFilterChips, withoutChip } from "./chips";
import { NO_FILTERS, type DirectoryFilters } from "./filters";

type MobileFilterBarProps = {
  className?: string;
  filters: DirectoryFilters;
  onChange: (filters: DirectoryFilters) => void;
  taxonomy?: Record<string, TaxonomyOption[]>;
  viewMode?: "list" | "map";
  onViewModeChange?: (viewMode: "list" | "map") => void;
};

export const MobileFilterBar: FC<MobileFilterBarProps> = ({
  className,
  filters,
  onChange,
  taxonomy,
  viewMode = "list",
  onViewModeChange,
}) => {
  const chips = toFilterChips(filters, taxonomy);
  const [chipsRef] = useEmblaCarousel({ axis: "x", dragFree: true, containScroll: "trimSnaps" });

  return (
    <div className={cn("flex flex-col gap-8", className)}>
      <div className="flex items-center gap-16">
        {/* No handler, no switch: the page can turn the map view off */}
        {onViewModeChange && (
          <Button
            variant="secondary-white"
            size="s"
            className="flex-1 font-normal text-neutral-black"
            icon={viewMode === "list" ? <MapIcon className="size-18" /> : <ListBulletIcon className="size-18" />}
            onClick={() => onViewModeChange(viewMode === "list" ? "map" : "list")}
          >
            {viewMode === "list" ? "Map View" : "List View"}
          </Button>
        )}
        <AllFiltersButton
          filters={filters}
          onChange={onChange}
          taxonomy={taxonomy}
          className="flex-1 font-normal text-neutral-black"
        />
      </div>

      {chips.length > 0 && (
        <div className="flex items-center gap-8">
          <div ref={chipsRef} className="min-w-0 flex-1 overflow-hidden">
            <div className="flex items-center gap-8">
              {chips.map((chip) => (
                <button
                  key={`${chip.key}:${chip.value}`}
                  type="button"
                  aria-label={`Remove ${chip.label}`}
                  className="shrink-0 cursor-pointer"
                  onClick={() => onChange(withoutChip(filters, chip))}
                >
                  <Tag size="m" className="text-12 whitespace-nowrap">
                    {chip.label}
                  </Tag>
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="h-24 shrink-0 cursor-pointer text-14 text-neutral-black underline"
            onClick={() => onChange(NO_FILTERS)}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};
